import React from 'react';
import {APP_ORIGIN} from '../../configs/Hosts';
import {AbMark} from '../brand/AbMark';

const PILLARS = [
    ['84 sonic dimensions', 'Every track is measured 84 ways: EQ balance, tone, rhythm, tempo, harmony, vocal texture, energy and dynamics. Together they form a fingerprint of how the song actually sounds.'],
    ['Zero popularity bias', 'Matches come from sound, not plays, trends or preferences. No charts, no click data, no herd effect. A brand-new artist sits next to a legend when they truly sound alike.'],
];

const StepIcon = ({children}) => (
    <svg viewBox="0 0 28 28" className="h-7 w-7" fill="none" stroke="#fa2d48" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        {children}
    </svg>
);

const STEPS = [
    ['Search', 'Pick any song', "Search 880,000 tracks, or start from whatever you're hearing right now.",
        <StepIcon key="s1"><circle cx="12" cy="12" r="7.5"/><path d="M17.5 17.5 L24 24"/></StepIcon>],
    ['Analysis', 'We measure its sound', '84 measurements of timbre, rhythm, harmony and texture. The song itself, fingerprinted.',
        <StepIcon key="s2"><path d="M3 14v0"/><path d="M3 11v6"/><path d="M8 7v14"/><path d="M13 3v22"/><path d="M18 8v12"/><path d="M25 12v4"/></StepIcon>],
    ['Results', 'Play what truly matches', 'A queue of sonic neighbors, ranked by closeness. It just keeps playing.',
        <StepIcon key="s3"><path d="M5 6.5 L10 9.5 L5 12.5 Z" fill="#fa2d48" stroke="none"/><path d="M14 9.5h9"/><path d="M5 17h18"/><path d="M5 23h13"/></StepIcon>],
];

const FEATURES = [
    ['🏷', 'Version-aware', 'Originals up front. Live takes and remixes are tagged and grouped, never polluting your queue.'],
    ['♪', 'Built on sound DNA', 'Every track measured from the audio itself, every match scored. You can see how close a neighbor really is.'],
    ['▶', 'Full songs via Apple Music', 'Connect your subscription for full tracks; previews for everyone either way.'],
];

const UP_NEXT = [
    ['Houdini', 'Eminem · 2024', '96%', true],
    ['Just Lose It', 'Eminem · 2004', '92%', false],
    ['Gimme the Loot', 'Biggie · 1994', '88%', false],
    ['Forgot About Dre', 'Dr. Dre · 1999', '87%', false],
];

const covers = [
    'linear-gradient(140deg,#7a4a33,#31151d)', 'linear-gradient(140deg,#23405a,#141c2b)',
    'linear-gradient(140deg,#5a2340,#26121b)', 'linear-gradient(140deg,#2b5a3d,#12261b)',
];

/** Mock product shot — a miniature of the player inside a browser frame. */
const ProductShot = () => (
    <div className="relative w-full max-w-[560px]">
        <div className="absolute -inset-10 rounded-[50px]"
             style={{background: 'radial-gradient(60% 60% at 50% 45%, rgba(250,45,72,.24), transparent 70%)'}}/>
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.14] shadow-[0_40px_100px_rgba(0,0,0,0.6)]">
            <div className="flex items-center gap-2 border-b border-white/[0.08] bg-[#0f0b08] px-3.5 py-2.5">
                {[0, 1, 2].map(i => <span key={i} className="h-2 w-2 rounded-full bg-white/[0.14]"/>)}
                <span className="mx-auto rounded-md bg-white/[0.08] px-7 py-1 text-[11px] text-white/50">abtunes.com</span>
            </div>
            <div className="flex h-[320px] bg-[#1b1410]"
                 style={{backgroundImage: 'radial-gradient(60% 90% at 55% 5%, rgba(250,45,72,.22), transparent 60%)'}}>
                <div className="hidden w-[150px] border-r border-white/[0.09] p-2.5 sm:block">
                    <div className="mb-2 text-[8.5px] font-extrabold uppercase tracking-[0.08em] text-white/[0.38]">History</div>
                    {[['Without Me', 'Eminem', true], ['Beautiful Lie', 'Evanescence', false], ['21 Questions', '50 Cent', false]].map(([t, a, on], i) => (
                        <div key={t} className={'mb-0.5 flex items-center gap-2 rounded-lg p-1.5 ' + (on ? 'bg-white/[0.09]' : '')}>
                            <span className="h-6 w-6 flex-none rounded-md" style={{background: covers[i % 4]}}/>
                            <span className="min-w-0 text-[9px] font-semibold leading-tight">
                                <span className="block truncate">{t}</span>
                                <span className="block text-[8px] font-medium text-white/40">{a}</span>
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex flex-1 flex-col items-center justify-center">
                    <div className="h-[104px] w-[104px] rounded-xl shadow-[0_14px_34px_rgba(0,0,0,0.5)]"
                         style={{background: covers[0]}}/>
                    <div className="mt-2.5 text-[15px] font-extrabold tracking-[-0.02em]">Without Me</div>
                    <div className="text-[10.5px] text-white/60">Eminem</div>
                    <div className="mt-0.5 text-[8.5px] text-white/[0.38]">2002 · Hip-Hop/Rap</div>
                    <div className="relative mt-3 h-1 w-[150px] rounded-full bg-white/[0.16]">
                        <span className="absolute inset-y-0 left-0 w-[62%] rounded-full bg-white"/>
                    </div>
                    <div className="mt-2.5 flex items-center gap-3.5 text-[11px] text-white/80">
                        ⏮ <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[10px] text-[#111]">❚❚</span> ⏭
                    </div>
                </div>
                <div className="hidden w-[172px] border-l border-white/[0.09] p-2.5 md:block">
                    <div className="mb-2 text-[8.5px] font-extrabold uppercase tracking-[0.08em] text-white/[0.38]">Up next · recommended</div>
                    {UP_NEXT.map(([t, a, m, hot], i) => (
                        <div key={t} className="mb-0.5 flex items-center gap-2 rounded-lg p-1.5">
                            <span className="h-6 w-6 flex-none rounded-md" style={{background: covers[(i + 2) % 4]}}/>
                            <span className="min-w-0 flex-1 text-[9px] font-semibold leading-tight">
                                <span className="block truncate">{t}</span>
                                <span className="block text-[8px] font-medium text-white/40">{a}</span>
                            </span>
                            <span className={'flex-none rounded px-1 text-[8px] font-extrabold tabular-nums ' + (hot ? 'bg-apple-red/30 text-[#ff97a5]' : 'bg-white/[0.12] text-white/80')}>{m}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

export default function Landing() {
    return (
        <div className="min-h-screen bg-ambient-base font-display text-white">
            {/* Nav */}
            <nav className="border-b border-white/10 bg-black/25 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1440px] items-center gap-5 px-6 py-5 sm:gap-8 lg:px-[90px]">
                    <span className="text-xl"><AbMark/></span>
                    <a href={APP_ORIGIN + '/login'} className="ml-auto text-sm font-semibold text-white/60 hover:text-white">Log in</a>
                    <a href={APP_ORIGIN + '/'} className="rounded-xl bg-apple-red px-5 py-2.5 text-sm font-bold">Open the player</a>
                </div>
            </nav>

            {/* Hero + stats share one gradient canvas so the glow fades out
                behind the stat cards instead of stopping at a hard seam. */}
            <div style={{
                backgroundImage:
                    'radial-gradient(45% 55% at 72% 12%, rgba(250,45,72,.20), transparent 68%),' +
                    'radial-gradient(38% 45% at 8% 75%, rgba(255,158,68,.10), transparent 65%)',
            }}>
            <div className="mx-auto flex max-w-[1440px] flex-col items-center gap-14 px-6 py-16 lg:flex-row lg:gap-16 lg:px-[90px] lg:py-[76px]">
                <div className="max-w-xl">
                    <h1 className="font-heading text-[46px] font-bold leading-[1.05] tracking-[-0.02em] sm:text-[60px]">
                        Find the songs that<br/><em className="not-italic text-apple-red">sound</em> like the<br/>ones you love
                    </h1>
                    <p className="mb-8 mt-5 max-w-[470px] text-lg text-white/[0.62]">
                        AbTunes listens to the music itself and plays what actually sounds close.
                        Not what other people clicked.
                    </p>
                    <a href={APP_ORIGIN + '/'}
                             className="inline-block rounded-[13px] bg-apple-red px-7 py-4 text-[15.5px] font-extrabold shadow-[0_14px_38px_rgba(250,45,72,0.35)]">
                        Start from a song
                    </a>
                    <a href="#how"
                       className="ml-3 inline-block rounded-[13px] border border-white/10 bg-white/5 px-6 py-4 text-[15.5px] font-semibold">
                        See how it works
                    </a>
                    <div className="mt-6 text-[13.5px] text-white/[0.38]">Free · Full songs with your Apple&nbsp;Music account</div>
                </div>
                <ProductShot/>
            </div>

            {/* What makes it different, still inside the gradient canvas */}
            <div className="mx-auto max-w-[1440px] px-6 pb-16 lg:px-[90px]">
                <div className="grid gap-5 md:grid-cols-2">
                    {PILLARS.map(([t, d]) => (
                        <div key={t} className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
                            <div className="font-heading text-[24px] font-bold tracking-[-0.015em] text-apple-red sm:text-[28px]">{t}</div>
                            <p className="mt-3 max-w-[560px] text-[15px] leading-relaxed text-white/[0.62]">{d}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-6 text-center text-[13px] text-white/[0.38]">
                    880,000+ songs analyzed · new music added weekly · a full catalog scan takes about 3 ms
                </div>
            </div>
            </div>

            {/* How it works */}
            <section id="how" className="border-t border-white/10 px-6 py-16 lg:px-[90px] lg:py-24">
                <div className="mx-auto max-w-[1440px]">
                <div className="text-xs font-extrabold uppercase tracking-[0.1em] text-apple-red">How it works</div>
                <h2 className="font-heading mb-10 mt-2 text-[30px] font-bold tracking-[-0.015em] sm:text-[38px]">
                    Three steps from one song to hours of them
                </h2>
                <div className="max-w-[760px]">
                    {STEPS.map(([label, t, d, icon], i) => (
                        <div key={label} className="relative flex gap-6 pb-12 last:pb-0 sm:gap-8 sm:pb-16">
                            {i < STEPS.length - 1 && (
                                <span className="absolute left-[27px] top-[64px] bottom-2 w-px bg-white/10" aria-hidden="true"/>
                            )}
                            <div className="flex h-[54px] w-[54px] flex-none items-center justify-center rounded-2xl border border-apple-red/20 bg-apple-red/15">
                                {icon}
                            </div>
                            <div className="pt-1">
                                <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-apple-red">Step {i + 1} · {label}</div>
                                <b className="font-heading mb-2 mt-1.5 block text-[21px] font-bold tracking-[-0.01em] sm:text-[24px]">{t}</b>
                                <p className="max-w-[520px] text-[14.5px] leading-relaxed text-white/[0.62]">{d}</p>
                            </div>
                        </div>
                    ))}
                </div>
                </div>
            </section>

            {/* Features */}
            <section className="border-t border-white/10 px-6 py-16 lg:px-[90px]">
                <div className="mx-auto max-w-[1440px]">
                <div className="text-xs font-extrabold uppercase tracking-[0.1em] text-apple-red">Why it feels different</div>
                <h2 className="font-heading mb-10 mt-2 text-[30px] font-bold tracking-[-0.015em] sm:text-[38px]">Recommendations with reasons</h2>
                <div className="grid max-w-[760px] gap-4">
                    {FEATURES.map(([icon, t, d]) => (
                        <div key={t} className="flex gap-4 rounded-[18px] border border-white/10 bg-white/5 p-6">
                            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-[13px] bg-apple-red/15 text-[19px]">{icon}</div>
                            <div>
                                <b className="mb-1 block text-[16.5px] font-bold tracking-[-0.01em]">{t}</b>
                                <p className="text-[13.5px] text-white/[0.62]">{d}</p>
                            </div>
                        </div>
                    ))}
                </div>
                </div>
            </section>

            {/* Industry */}
            <div className="px-6 pb-16 lg:px-[90px]">
            <div id="industry" className="mx-auto flex max-w-[1440px] flex-col items-start gap-10 rounded-3xl border border-white/10 bg-[#0f0b08] p-9 lg:p-14">
                <div>
                    <div className="text-xs font-extrabold uppercase tracking-[0.1em] text-apple-red">For streaming platforms, music companies &amp; labels</div>
                    <h2 className="font-heading mb-3.5 mt-2 text-[28px] font-bold tracking-[-0.015em] sm:text-[34px]">The same engine, as an API</h2>
                    <p className="max-w-[520px] text-[14.5px] text-white/[0.62]">
                        Catalog matching and recommendations for your own service: ISRC in, ranked sonic neighbors out.
                        Every track enriched with BPM, key and version tags.
                    </p>
                    <a href="mailto:hello@abtunes.com"
                       className="mt-6 inline-block rounded-xl bg-apple-red px-6 py-3 text-sm font-extrabold">Talk to us →</a>
                </div>
                <pre className="w-full min-w-0 max-w-full overflow-x-auto rounded-2xl border border-white/10 bg-[#0b0f1c] p-5 font-mono text-[10.5px] leading-[1.8] text-[#d9a0a8] sm:p-6 sm:text-xs lg:max-w-[640px]">
{`GET /v2/recommendations?isrc=USUM71703861

{ "matches": [
  { "isrc": "USIR20400274", "score": 0.96 },
  { "isrc": "USIR10211066", "score": 0.92 } ] }`}
                </pre>
            </div>
            </div>

            {/* Final CTA */}
            <div className="px-6 py-20 text-center"
                 style={{backgroundImage: 'radial-gradient(50% 80% at 50% 100%, rgba(250,45,72,.14), transparent 65%)'}}>
                <div className="text-xs font-extrabold uppercase tracking-[0.1em] text-apple-red">Ready?</div>
                <h2 className="font-heading mb-4 mt-2 text-[30px] font-bold tracking-[-0.015em] sm:text-[38px]">
                    Your next favorite song already exists
                </h2>
                <a href={APP_ORIGIN + '/'}
                         className="inline-block rounded-[13px] bg-apple-red px-7 py-4 text-[15.5px] font-extrabold shadow-[0_14px_38px_rgba(250,45,72,0.35)]">
                    Start from one you love
                </a>
            </div>

            <footer className="border-t border-white/10">
                <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-x-7 gap-y-2 px-6 py-6 text-[13px] text-white/[0.38] lg:px-[90px]">
                    <span className="text-base"><AbMark size={20}/></span>
                    <span>Privacy</span><span>Terms</span>
                    <span className="ml-auto">© 2026 AbTunes</span>
                </div>
            </footer>
        </div>
    );
}
